// Configuration TVA lors de la création / édition d'un business

import React, { useState, useEffect } from 'react';
import {
  Card,
  Select,
  Switch,
  Input,
  Button,
  Space,
  Typography,
  Alert,
  Row,
  Col,
  InputNumber,
  Tag,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  CalculatorOutlined,
} from '@ant-design/icons';
import { PREDEFINED_TAX_RATES, TaxRate } from '../../../types/tax';

const { Title, Text } = Typography;

interface TaxConfigurationStepProps {
  value?: {
    defaultRateId: string;
    selectedRates: string[];
    rates: TaxRate[];
    displayMode: 'inclusive' | 'exclusive';
    showTaxOnReceipts: boolean;
    companyTaxNumber?: string;
  };
  onChange?: (value: any) => void;
}

const getIdFromPredefinedIndex = (index: number): string => {
  const rate = PREDEFINED_TAX_RATES[index];
  if (rate.name.includes('Cameroun')) return 'cameroon_standard';
  if (rate.name.includes('Sénégal')) return 'senegal_standard';
  if (rate.name.includes("Côte d'Ivoire")) return 'ivory_coast_standard';
  if (rate.name.includes('Burkina')) return 'burkina_standard';
  if (rate.name.includes('Mali')) return 'mali_standard';
  if (rate.name.includes('Exonéré')) return 'tax_exempt';
  return `rate_${index}`;
};

const resolveRatesFromValue = (value: {
  selectedRates?: string[];
  rates?: TaxRate[];
}): TaxRate[] => {
  // Priorité aux objets rates déjà persistés (source de vérité)
  if (value.rates && value.rates.length > 0) {
    return value.rates.map((rate) => ({
      ...rate,
      createdAt: rate.createdAt ? new Date(rate.createdAt as unknown as string | number | Date) : new Date(),
    }));
  }

  const selectedIds = value.selectedRates || [];
  if (selectedIds.length === 0) {
    const cameroonRate = PREDEFINED_TAX_RATES.find((r) => r.name.includes('Cameroun'));
    if (!cameroonRate) return [];
    return [
      {
        ...cameroonRate,
        id: 'cameroon_standard',
        createdAt: new Date(),
      },
    ];
  }

  return PREDEFINED_TAX_RATES.map((rate, index) => {
    const id = getIdFromPredefinedIndex(index);
    if (!selectedIds.includes(id)) return null;
    return {
      ...rate,
      id,
      createdAt: new Date(),
    };
  }).filter(Boolean) as TaxRate[];
};

const TaxConfigurationStep: React.FC<TaxConfigurationStepProps> = ({
  value = {
    defaultRateId: '',
    selectedRates: ['cameroon_standard'],
    rates: [],
    displayMode: 'inclusive',
    showTaxOnReceipts: true,
    companyTaxNumber: '',
  },
  onChange,
}) => {
  const [selectedRates, setSelectedRates] = useState<TaxRate[]>(() => resolveRatesFromValue(value));
  const [customRate, setCustomRate] = useState({ name: '', rate: 0 });
  const [showCustomForm, setShowCustomForm] = useState(false);

  // Resynchroniser l'UI quand la valeur externe change (édition d'un business existant)
  const ratesSignature = (value.rates || [])
    .map((r) => `${r.id}:${r.rate}`)
    .join('|');
  const selectedRatesSignature = (value.selectedRates || []).join('|');

  useEffect(() => {
    const rates = resolveRatesFromValue(value);
    setSelectedRates(rates);

    // Compléter le form uniquement si les objets rates manquent encore
    if ((!value.rates || value.rates.length === 0) && rates.length > 0) {
      onChange?.({
        ...value,
        selectedRates: rates.map((r) => r.id),
        rates,
        defaultRateId: value.defaultRateId || rates[0].id,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync on tax rates identity
  }, [value.defaultRateId, value.displayMode, ratesSignature, selectedRatesSignature]);

  const handleChange = (newValue: any) => {
    onChange?.(newValue);
  };

  const handleAddPredefinedRate = (rateIndex: number) => {
    const rate = PREDEFINED_TAX_RATES[rateIndex];
    const rateId = getIdFromPredefinedIndex(rateIndex);

    if (selectedRates.find((r) => r.id === rateId)) return;

    const newRate: TaxRate = {
      ...rate,
      id: rateId,
      createdAt: new Date(),
    };

    const newSelectedRates = [...selectedRates, newRate];
    setSelectedRates(newSelectedRates);

    handleChange({
      ...value,
      selectedRates: newSelectedRates.map((r) => r.id),
      rates: newSelectedRates,
      defaultRateId: value.defaultRateId || rateId,
    });
  };

  const handleAddCustomRate = () => {
    if (!customRate.name || customRate.rate <= 0) return;

    const rateId = `custom_${Date.now()}`;
    const newRate: TaxRate = {
      id: rateId,
      name: customRate.name,
      rate: customRate.rate,
      description: 'Taux personnalisé',
      isDefault: false,
      isActive: true,
      createdAt: new Date(),
    };

    const newSelectedRates = [...selectedRates, newRate];
    setSelectedRates(newSelectedRates);

    handleChange({
      ...value,
      selectedRates: newSelectedRates.map((r) => r.id),
      rates: newSelectedRates,
    });

    setCustomRate({ name: '', rate: 0 });
    setShowCustomForm(false);
  };

  const handleRemoveRate = (rateId: string) => {
    const newSelectedRates = selectedRates.filter((r) => r.id !== rateId);
    setSelectedRates(newSelectedRates);

    let newDefaultId = value.defaultRateId;
    if (value.defaultRateId === rateId && newSelectedRates.length > 0) {
      newDefaultId = newSelectedRates[0].id;
    }

    handleChange({
      ...value,
      selectedRates: newSelectedRates.map((r) => r.id),
      rates: newSelectedRates,
      defaultRateId: newDefaultId,
    });
  };

  const handleDisplayModeChange = (mode: 'inclusive' | 'exclusive') => {
    handleChange({ ...value, displayMode: mode });
  };

  return (
    <Card title="Configuration de la TVA" size="small">
      <Alert
        message="Configuration des taux de TVA"
        description="Définissez les taux de TVA disponibles pour ce business. Vous pourrez modifier ces paramètres plus tard."
        type="info"
        showIcon
        style={{ marginBottom: '24px' }}
      />

      <Card size="small" style={{ marginBottom: '16px' }}>
        <Title level={5} style={{ margin: 0, marginBottom: '12px' }}>
          <CalculatorOutlined /> Mode d'affichage des prix
        </Title>

        <Row gutter={16}>
          <Col span={12}>
            <Button
              type={value.displayMode === 'inclusive' ? 'primary' : 'default'}
              block
              onClick={() => handleDisplayModeChange('inclusive')}
            >
              Prix TTC (TVA incluse)
            </Button>
            <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: '4px' }}>
              Recommandé pour la vente au détail
            </Text>
          </Col>
          <Col span={12}>
            <Button
              type={value.displayMode === 'exclusive' ? 'primary' : 'default'}
              block
              onClick={() => handleDisplayModeChange('exclusive')}
            >
              Prix HT (TVA séparée)
            </Button>
            <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: '4px' }}>
              Recommandé pour le B2B
            </Text>
          </Col>
        </Row>
      </Card>

      <Card size="small" style={{ marginBottom: '16px' }}>
        <Title level={5} style={{ margin: 0, marginBottom: '12px' }}>
          Taux de TVA configurés
        </Title>

        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          {selectedRates.map((record) => (
            <Card key={record.id} size="small" styles={{ body: { padding: 12 } }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 12,
                  flexWrap: 'wrap',
                  alignItems: 'flex-start',
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Space wrap>
                    <Text strong>{record.name}</Text>
                    <Text strong>{record.rate}%</Text>
                    {record.id === value.defaultRateId && <Tag color="blue">Par défaut</Tag>}
                  </Space>
                  {record.description && (
                    <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>{record.description}</div>
                  )}
                </div>
                <Space wrap>
                  <Button
                    type={record.id === value.defaultRateId ? 'primary' : 'default'}
                    size="small"
                    onClick={() => handleChange({ ...value, defaultRateId: record.id })}
                  >
                    {record.id === value.defaultRateId ? 'Par défaut' : 'Définir par défaut'}
                  </Button>
                  <Button
                    type="text"
                    danger
                    size="small"
                    icon={<DeleteOutlined />}
                    onClick={() => handleRemoveRate(record.id)}
                    disabled={selectedRates.length === 1}
                  />
                </Space>
              </div>
            </Card>
          ))}
        </Space>
      </Card>

      <Card size="small" style={{ marginBottom: '16px' }}>
        <Title level={5} style={{ margin: 0, marginBottom: '12px' }}>
          Ajouter des taux prédéfinis
        </Title>

        <Row gutter={[8, 8]}>
          {PREDEFINED_TAX_RATES.map((rate, index) => {
            const rateId = getIdFromPredefinedIndex(index);
            const isSelected = selectedRates.find((r) => r.id === rateId);

            return (
              <Col key={index}>
                <Button
                  size="small"
                  disabled={!!isSelected}
                  onClick={() => handleAddPredefinedRate(index)}
                >
                  {rate.name} ({rate.rate}%)
                </Button>
              </Col>
            );
          })}
        </Row>
      </Card>

      <Card size="small" style={{ marginBottom: '16px' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '12px',
          }}
        >
          <Title level={5} style={{ margin: 0 }}>
            Taux personnalisé
          </Title>
          <Button
            type="dashed"
            size="small"
            icon={<PlusOutlined />}
            onClick={() => setShowCustomForm(!showCustomForm)}
          >
            Ajouter un taux
          </Button>
        </div>

        {showCustomForm && (
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Input
                placeholder="Nom du taux"
                value={customRate.name}
                onChange={(e) => setCustomRate({ ...customRate, name: e.target.value })}
              />
            </Col>
            <Col span={8}>
              <InputNumber
                placeholder="Taux (%)"
                style={{ width: '100%' }}
                min={0}
                max={100}
                step={0.01}
                value={customRate.rate}
                onChange={(v) => setCustomRate({ ...customRate, rate: v || 0 })}
              />
            </Col>
            <Col span={4}>
              <Button
                type="primary"
                onClick={handleAddCustomRate}
                disabled={!customRate.name || customRate.rate <= 0}
              >
                Ajouter
              </Button>
            </Col>
          </Row>
        )}
      </Card>

      <Card size="small">
        <Title level={5} style={{ margin: 0, marginBottom: '16px' }}>
          Options supplémentaires
        </Title>

        <Row gutter={[16, 16]}>
          <Col span={24}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <Text strong>Afficher le détail TVA sur les tickets</Text>
                <br />
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  Recommandé pour la transparence
                </Text>
              </div>
              <Switch
                checked={value.showTaxOnReceipts}
                onChange={(checked) => handleChange({ ...value, showTaxOnReceipts: checked })}
              />
            </div>
          </Col>

          <Col span={24}>
            <Text strong>Numéro de TVA de l'entreprise (optionnel)</Text>
            <Input
              placeholder="Ex: TVA123456789"
              style={{ marginTop: '8px' }}
              value={value.companyTaxNumber}
              onChange={(e) => handleChange({ ...value, companyTaxNumber: e.target.value })}
            />
          </Col>
        </Row>
      </Card>
    </Card>
  );
};

export default TaxConfigurationStep;
